import {
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { IRootScopeService, IScope } from 'angular';
import { filter, includes, mapValues, startCase } from 'lodash';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ISubscription } from 'rxjs/Subscription';
import { UtilitiesService } from 'Src/ng2/shared/services/utilities/utilities.service';
import { IStudentActivity } from 'Src/ng2/shared/typings/interfaces/student-activity.interface';
import { IStudentAssessment } from 'Src/ng2/shared/typings/interfaces/student-assessment.interface';
import { IDocLog } from '../../../app/shared/models/doc-log/doc-log';
import { LocalStorageService } from '../../shared/services/web-storage/local-storage/local-storage.service';
import { ISchool } from '../../shared/typings/interfaces/school.interface';
import { IStudentSupport } from '../../shared/typings/interfaces/student-support.interface';
import { IStudentPath } from '../../shared/typings/interfaces/studentPaths.interface';
import { ISchoolSupport } from '../../shared/typings/interfaces/support.interface';
import { IUser } from '../../shared/typings/interfaces/user.interface';
import { getStudentSupportsEntities, IUpdatePayload, UpdateFlag } from '../../store';
import { unsubscribeComponent } from './../../shared/helpers/unsubscribe-decorator/unsubscribe-decorators.helper';
import { ImSchool } from './../../shared/services/im-models/im-school';
import { ImStudent } from './../../shared/services/im-models/im-student.service';
import { SideNavService } from './../../shared/services/side-nav/side-nav.service';
import { IFlag } from './../../shared/typings/interfaces/flags.interface';
import { IMilestone } from './../../shared/typings/interfaces/milestones.interface';
import { CreateStudentSupportSuccess } from './../../store/actions/student-supports-actions';
import { StudentPanelsConfig } from './student-panels.config';

interface Panels {
  [propName: string]: { visible: boolean };
}

@Component({
  selector: 'v3-student-container',
  template: require('./student-container.component.html'),
  styles: [require('./student-container.component.scss')],
  encapsulation: ViewEncapsulation.None,
})
@unsubscribeComponent
export class V3StudentContainer implements OnInit {
  @Input() studentList: any;
  @Input() currentStudent: any;
  @Input() currentStudentId: string;
  @Input() currentSchool: ISchool;
  @Input() schoolUsers: IUser;
  @Input() students;
  @Input() studentDiffs;
  @Input() studentGapPlans;
  @Input() studentSupports;
  @Input() currentUser: IUser;
  @Input() ssvExitState: string;
  @Input() currentState: string;
  @Input() supports: ISchoolSupport[];
  @Input() flags: IFlag[];
  @Input() docLogs$: Observable<IDocLog[]>;
  @Input() studentPaths$: Observable<any>;
  @Input() collegePaths$: Observable<any>;
  @Input() careerPaths$: Observable<any>;
  @Input() milestones$: Observable<any>;
  @Input() studentActivities$: Observable<IStudentActivity[]>;
  @Input() studentAssessments$: Observable<IStudentAssessment[]>;
  @Input() studentSupports$: Observable<IStudentSupport[]>;
  @Input() isRestricted: boolean;
  @Input() isSummerSchoolStudent: boolean;

  @Output() saveStudent: EventEmitter<any> = new EventEmitter();

  noteForPanel;
  panels: Panels;
  collapsed: boolean;
  moreThanOneStudent: boolean;
  milestones: IMilestone[];
  studentPaths: IStudentPath[];
  studentActivities: IStudentActivity[];
  studentAssessments: IStudentAssessment[];
  docLogs: IDocLog[];
  student;
  setPanelShadowStyle: boolean = false;

  studentSupportsSubscription: ISubscription;

  @ViewChild('panelContainer', { static: true }) panelContainer: ElementRef;

  constructor(
    @Inject('$rootScope') private $rootScope: IRootScopeService,
    private sideNavService: SideNavService,
    private store: Store<any>,
    private localStorage: LocalStorageService,
    private imStudent: ImStudent,
    private imSchool: ImSchool,
    private utilitiesService: UtilitiesService,
  ) {
    //
  }

  public ngOnInit(): void {
    this.student = this.getStudentHeaderDetails();
    this.sideNavService.hideSideNav();
    this.moreThanOneStudent = this.students && this.students.length > 1 ? true : false;

    // Get panels that are visible based on the student type (HS/MS/ES)
    // as well as whether they are attending their home school
    this.panels = this.getVisiblePanels();

    const keyExistsInStorage = this.localStorage.checkForItem('ssvCollapsedPanels');
    if (keyExistsInStorage) {
      const localStorageHasCollapsedPanels = this.localStorage.getItem('ssvCollapsedPanels').length;
      localStorageHasCollapsedPanels ? (this.collapsed = true) : (this.collapsed = false);
    }

    const destroy$: Subject<boolean> = new Subject<boolean>();

    if (!this.isSummerSchoolStudent) {
      this.milestones$.pipe(takeUntil(destroy$)).subscribe((milestones: IMilestone[]) => {
        this.milestones = milestones;
      });
      this.studentPaths$.pipe(takeUntil(destroy$)).subscribe((studentPaths: IStudentPath[]) => {
        this.studentPaths = studentPaths;
      });
      this.docLogs$.pipe(takeUntil(destroy$)).subscribe((docLogs: IDocLog[]) => {
        this.docLogs = docLogs;
      });
      this.studentActivities$.pipe(takeUntil(destroy$)).subscribe((studentActivities: IStudentActivity[]) => {
        this.studentActivities = studentActivities;
      });
      this.studentAssessments$.pipe(takeUntil(destroy$)).subscribe((studentAssessments: IStudentAssessment[]) => {
        this.studentAssessments = filter(
          studentAssessments,
          studentAssessment => studentAssessment.student.studentId === this.currentStudent._id,
        );
      });
    }
  }

  getVisiblePanels(): Panels {
    let studentType: string;
    if (this.currentStudent.isHS) studentType = 'HS';
    else if (this.currentStudent.isMS) studentType = 'MS';
    else studentType = 'ES';

    const visiblePanels = mapValues(StudentPanelsConfig, ({ studentTypes, otherSchoolTypes }) => {
      // Is panel valid for given student type ('ES', 'MS', 'HS')
      const isPanelValidForStudent = includes(studentTypes, studentType);

      // Is panel valid for an other school type (summer school)
      let isPanelValidOtherSchoolType = true;
      if (this.isSummerSchoolStudent) {
        isPanelValidOtherSchoolType = otherSchoolTypes.includes('summer');
      }
      const visible = isPanelValidForStudent && isPanelValidOtherSchoolType;
      return { visible };
    });
    return visiblePanels;
  }

  ngOnChanges(changes) {
    if (changes.currentStudent) this.student = this.getStudentHeaderDetails();
  }

  getStudentHeaderDetails() {
    const {
      studentDetails: {
        name: { last: lastName, first: firstName },
        classOf,
        officialClass,
        grade,
        learningPreference,
      },
      spedDetails,
      ellDetails: { isEll },
      isHS,
    } = this.currentStudent;
    const first = startCase(firstName.toLowerCase());
    const last = startCase(lastName.toLowerCase());
    const displayId = this.currentStudent.studentId || '—';
    const sped = this.utilitiesService.getBooleanOrDash(spedDetails.isSped);
    let currentGradPlan;
    if (this.imSchool.isTransferSchool(this.currentSchool)) {
      currentGradPlan = this.imStudent.getCurrentGradPlanTransfer(this.currentStudent);
    } else {
      currentGradPlan = this.imStudent.getCurrentGradPlan(this.currentStudent);
    }
    if (!currentGradPlan || (currentGradPlan && currentGradPlan.trim() === '')) {
      currentGradPlan = '—';
    }
    const safetyNet = this.imStudent.isSafetyNetEligible(this.currentStudent) ? 'ELIGIBLE' : 'NOT ELIGIBLE';
    const ellValue = this.utilitiesService.getBooleanOrDash(isEll);
    const headerDetails = {
      last,
      first,
      displayId,
      ellValue,
      isHS,
      sped,
      currentGradPlan,
      safetyNet,
      classOf: classOf || '—',
      officialClass: officialClass || '—',
      grade: grade || '—',
      learningPreference: learningPreference || '—',
    };
    return headerDetails;
  }

  // another approach is to use ngOnChanges (dvn)
  updateStudent() {
    this.student = this.getStudentHeaderDetails();
  }

  public updateFlag(patch: IUpdatePayload<IFlag>) {
    this.store.dispatch(new UpdateFlag(patch));
  }

  public onNoteCreated(note) {
    this.noteForPanel = note;
    // temp solution to update notes in notes panel
    // TODO: notes should be moved to resolve block and ngrx store (CM)
    this.$rootScope.$broadcast('newNote', note);
  }

  // All of this logic should not be necessary once student profile is on the ngrx store.
  // Ideally, this would all happen via an action that would update the ngrx student profile slice (CM).
  public onStudentSupportCreated(options: IStudentSupport) {
    this.store.dispatch(new CreateStudentSupportSuccess(options));

    // get the latest student supports from the store
    const studentSupports = getStudentSupportsEntities;
    const studentSupports$ = this.store.select(studentSupports);

    studentSupports$.subscribe(studentSupports => {
      this.studentSupports = this.setStudentSupports(studentSupports);
    });
  }

  setStudentSupports(studentSupports): IStudentSupport[] {
    return filter(studentSupports, (studentSupport: IStudentSupport) => {
      return studentSupport.student.studentId === this.currentStudent._id;
    });
  }

  // listen for emitted toggle events and set panel visibility
  public onCollapsedPanelChanges($event: { name?: string; panels: string[] }) {
    const localStorageHasCollapsedPanels = $event.panels.length;
    localStorageHasCollapsedPanels ? (this.collapsed = true) : (this.collapsed = false);
  }

  onScroll() {
    const scrollPos = this.panelContainer.nativeElement.scrollTop;
    this.setPanelShadowStyle = scrollPos > 10;
  }
}
